import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "sonner";
import AppShell from "@/components/AppShell";
import { BrandMark } from "@/components/BrandMark";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Clients from "@/pages/Clients";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Tasks from "@/pages/Tasks";
import BOQ from "@/pages/BOQ";
import Invoices from "@/pages/Invoices";
import Expenses from "@/pages/Expenses";
import Inventory from "@/pages/Inventory";
import Employees from "@/pages/Employees";
import Attendance from "@/pages/Attendance";
import Reports from "@/pages/Reports";
import Library from "@/pages/Library";
import MaterialLibrary from "@/pages/MaterialLibrary";
import PurchaseOrders from "@/pages/PurchaseOrders";
import Accounting from "@/pages/Accounting";
import Settings from "@/pages/Settings";
import ClientPortal from "@/pages/ClientPortal";
import UserManagement from "@/pages/UserManagement";
import HRManagement from "@/pages/HRManagement";
import SEOHead from "@/components/SEOHead";
import { ShieldAlert } from "lucide-react";

function NoAccess() {
  return (
    <div data-testid="no-access" className="min-h-[60vh] flex flex-col items-center justify-center text-center gap-4 px-6">
      <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center">
        <ShieldAlert className="w-7 h-7 text-destructive" />
      </div>
      <div>
        <h2 className="font-display text-2xl font-semibold">Access restricted</h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-md">
          You don't have permission to view this module. Please contact your administrator if you need access.
        </p>
      </div>
      <a href="/" className="text-sm text-primary hover:underline" data-testid="no-access-home">Back to Dashboard</a>
    </div>
  );
}

// Route-level gate: blocks direct-URL access to a module the user lacks `view` on.
function Guarded({ module, children }) {
  const { hasPermission } = useAuth();
  if (module && !hasPermission(module, "view")) return <NoAccess />;
  return children;
}

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
      <BrandMark variant="hero" showTagline />
      <div className="text-muted-foreground text-xs uppercase tracking-[0.25em]">Loading ERP…</div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell><Outlet /></AppShell>;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <SEOHead />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<Protected />}>
              <Route path="/" element={<Guarded module="dashboard"><Dashboard /></Guarded>} />
              <Route path="/leads" element={<Guarded module="crm"><Leads /></Guarded>} />
              <Route path="/leads/:id" element={<Guarded module="crm"><LeadDetail /></Guarded>} />
              <Route path="/clients" element={<Guarded module="clients"><Clients /></Guarded>} />
              <Route path="/projects" element={<Guarded module="projects"><Projects /></Guarded>} />
              <Route path="/projects/:id" element={<Guarded module="projects"><ProjectDetail /></Guarded>} />
              <Route path="/tasks" element={<Guarded module="tasks"><Tasks /></Guarded>} />
              <Route path="/boq" element={<Guarded module="boq"><BOQ /></Guarded>} />
              <Route path="/invoices" element={<Guarded module="invoices"><Invoices /></Guarded>} />
              <Route path="/expenses" element={<Guarded module="expenses"><Expenses /></Guarded>} />
              <Route path="/inventory" element={<Guarded module="inventory"><Inventory /></Guarded>} />
              <Route path="/employees" element={<Guarded module="employees"><Employees /></Guarded>} />
              <Route path="/hr" element={<Guarded module="employees"><HRManagement /></Guarded>} />
              <Route path="/attendance" element={<Guarded module="attendance"><Attendance /></Guarded>} />
              <Route path="/reports" element={<Guarded module="reports"><Reports /></Guarded>} />
              <Route path="/library" element={<Guarded module="library"><Library /></Guarded>} />
              <Route path="/material-library" element={<Guarded module="library"><MaterialLibrary /></Guarded>} />
              <Route path="/purchase-orders" element={<Guarded module="inventory"><PurchaseOrders /></Guarded>} />
              <Route path="/accounting" element={<Guarded module="finance"><Accounting /></Guarded>} />
              <Route path="/settings" element={<Guarded module="settings"><Settings /></Guarded>} />
              <Route path="/users" element={<Guarded module="users"><UserManagement /></Guarded>} />
              <Route path="/portal" element={<Guarded module="portal"><ClientPortal /></Guarded>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
